'use client';

import { useState } from 'react';

import { recoverPhone, requestPhoneOtp } from '@/actions';
import type {
  OtpVerificationModalProps,
  OtpVerifyResult,
} from '@/components/OtpVerificationModal';
import { useWhitelabelConfig } from '@/hooks/useWhitelabelConfig';
import { toast, useForm, zodResolver, type Control, type FieldErrors } from '@/libs';
import { OtpScope, ValidationKey } from '@/types/enums';
import { recoveryPhoneSchema, type RecoveryPhoneFormData } from '@/types/models';
import { OTP_HEADER_CURRENT_PHONE } from '@/types/models/OtpChallenge';
import { parseBoolean } from '@/utils';
import { addDDIToPhone, parseDigits } from '@/utils/formatters';

import { RECOVERY_MESSAGES } from '../constants';

type UseRecoveryPhoneFormProps = {
  cpf: string;
  onSuccess: () => void;
};

type UseRecoveryPhoneFormReturn = {
  control: Control<RecoveryPhoneFormData>;
  errors: FieldErrors<RecoveryPhoneFormData>;
  isLoading: boolean;
  otpVerification: OtpVerificationModalProps | null;
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
};

export const useRecoveryPhoneForm = ({
  cpf,
  onSuccess,
}: UseRecoveryPhoneFormProps): UseRecoveryPhoneFormReturn => {
  const { getValue } = useWhitelabelConfig();
  const [otpVerification, setOtpVerification] =
    useState<OtpVerificationModalProps | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RecoveryPhoneFormData>({
    resolver: zodResolver(recoveryPhoneSchema),
    defaultValues: {
      newPhone: '',
      confirmNewPhone: '',
    },
  });

  const showRecoverPhoneError = (errorMessage?: string) => {
    const isDuplicatePhone =
      !!errorMessage && /diferente do telefone atual|já cadastrad/i.test(errorMessage);

    toast.error(
      isDuplicatePhone
        ? RECOVERY_MESSAGES.PHONE_ALREADY_REGISTERED
        : RECOVERY_MESSAGES.PHONE_UPDATE_ERROR,
    );
  };

  const onSubmit = async (data: RecoveryPhoneFormData) => {
    const payload = {
      cpf: parseDigits(cpf),
      newPhone: addDDIToPhone(data.newPhone),
    };

    try {
      const otpRequired = parseBoolean(
        getValue<boolean>(ValidationKey.VALIDATION_OTP_REQUIRED_PHONE_RECOVERY),
        false,
      );

      if (!otpRequired) {
        const result = await recoverPhone(payload);

        if (!result?.success) {
          showRecoverPhoneError(result?.errorMessage);
          return;
        }

        toast.success(RECOVERY_MESSAGES.PHONE_UPDATED);
        onSuccess();
        return;
      }

      const otpRequest = await requestPhoneOtp({
        scope: OtpScope.PHONE_RECOVERY,
        cpf: payload.cpf,
        newPhone: payload.newPhone,
      });

      if (otpRequest.error || !otpRequest.data) {
        toast.error(
          'Não foi possível enviar o código por SMS. Tente novamente mais tarde.',
        );
        return;
      }

      const verifyRecoveryOtp = async (code: string): Promise<OtpVerifyResult> => {
        const result = await recoverPhone(payload, {
          [OTP_HEADER_CURRENT_PHONE]: parseDigits(code),
        });

        if (result.success) {
          return 'success';
        }

        const resultCode = result.otpChallenge?.code || '';

        if (result.status === 403 || resultCode.includes('INVALID')) {
          return 'invalid';
        }

        if (result.status === 429 || resultCode.includes('MAX_ATTEMPTS')) {
          return 'blocked';
        }

        showRecoverPhoneError(result.errorMessage);
        return 'error';
      };

      setOtpVerification({
        maskedPhone: otpRequest.data.maskedPhone,
        title: 'Confirme o novo telefone',
        description: (maskedPhone) =>
          maskedPhone
            ? `Digite o código enviado para o novo telefone ${maskedPhone}.`
            : 'Digite o código enviado para o novo telefone.',
        onVerify: verifyRecoveryOtp,
        onResend: async () => {
          const result = await requestPhoneOtp({
            scope: OtpScope.PHONE_RECOVERY,
            cpf: payload.cpf,
            newPhone: payload.newPhone,
          });

          return result.data?.maskedPhone;
        },
        onClose: () => setOtpVerification(null),
        onSuccess: () => {
          setOtpVerification(null);
          toast.success(RECOVERY_MESSAGES.PHONE_UPDATED);
          onSuccess();
        },
      });
    } catch {
      toast.error(RECOVERY_MESSAGES.PHONE_UPDATE_ERROR);
    }
  };

  return {
    control,
    errors,
    isLoading: isSubmitting,
    otpVerification,
    handleSubmit: handleSubmit(onSubmit),
  };
};
